/**
 * Per-grid in-memory channel that lets one filter on a grid read another's
 * UI state without prop-drilling through every component. Used today by
 * KioskFilter / LocationFilter to react to the "Include Test Kiosks"
 * `FilterShowCheckBox` sibling: when it's unchecked, those filters trim
 * test rigs out of their suggestion list.
 *
 * Design constraints from the user:
 *   • No prop drilling — filters shouldn't have to accept a `siblingParams`
 *     prop that re-renders them on every keystroke in any other filter.
 *   • Subscribers only re-render when THEIR key flips. Other filter
 *     activity must not propagate. We build on `useSyncExternalStore`,
 *     which subscribes one listener per key.
 *   • Read the checkbox's UI state, not its emitted InputParam. A default
 *     FilterShowCheckBox emits zero params when checked (onChecked=undefined),
 *     so the InputParam stream is ambiguous; we publish the boolean directly.
 *
 * Scope: one store per `<DataGrid>` (created via useRef in dataGrid.tsx and
 * exposed through FilterSiblingProvider). Cross-grid isolation falls out of
 * provider scoping — no globals.
 */

import { createContext, useContext, useEffect, useSyncExternalStore } from "react";

type Key = string; // canonical: filter label, lowercased + trimmed
type Listener = () => void;

export interface FilterSiblingStore {
  get(key: Key): boolean | undefined;
  set(key: Key, value: boolean | undefined): void;
  subscribe(key: Key, l: Listener): () => void;
}

export function createFilterSiblingStore(): FilterSiblingStore {
  const values = new Map<Key, boolean | undefined>();
  const listeners = new Map<Key, Set<Listener>>();
  return {
    get: (k) => values.get(k),
    set: (k, v) => {
      if (values.get(k) === v) return;
      values.set(k, v);
      listeners.get(k)?.forEach((l) => l());
    },
    subscribe: (k, l) => {
      let set = listeners.get(k);
      if (!set) { set = new Set(); listeners.set(k, set); }
      set.add(l);
      return () => { set!.delete(l); };
    },
  };
}

const FilterSiblingContext = createContext<FilterSiblingStore | null>(null);
export const FilterSiblingProvider = FilterSiblingContext.Provider;

function canonKey(label: string | undefined): Key {
  return (label ?? "").toLowerCase().trim();
}

const NOOP = () => () => {};

/**
 * Read a sibling checkbox state by its FilterText label. Returns `undefined`
 * when no sibling with that label is mounted (or there's no provider at all),
 * so callers can distinguish "absent" from "present and false".
 *
 * Re-renders the calling component ONLY when this specific key flips —
 * standard `useSyncExternalStore` single-key subscription.
 */
export function useSiblingCheckbox(label: string): boolean | undefined {
  const store = useContext(FilterSiblingContext);
  const key = canonKey(label);
  return useSyncExternalStore(
    store ? (l) => store.subscribe(key, l) : NOOP,
    () => store?.get(key),
    () => undefined,
  );
}

/**
 * Publish a sibling checkbox state. Wired into ShowCheckboxFilter so its
 * `FilterText` becomes a sibling key on the grid's store. `enabled` is
 * driven by the filter's type (only the checkbox variants opt in) so we
 * don't pollute the store with unrelated filters.
 */
export function usePublishSiblingCheckbox(
  label: string | undefined,
  checked: boolean,
  enabled: boolean,
): void {
  const store = useContext(FilterSiblingContext);
  const key = canonKey(label);
  useEffect(() => {
    if (!store || !enabled || !key) return;
    store.set(key, checked);
    return () => { store.set(key, undefined); };
  }, [store, key, checked, enabled]);
}
